import { WBButton, WBCollapse, WBFlex, WBTypography } from '@admiin-com/ds-web';
import { styled } from '@mui/material';
import React from 'react';
import LottieAnimation from '../LottieAnimation/LottieAnimation';

interface OCRSanningProps {
  isIn: boolean;
  loop?: boolean;
  lottiePath?: string;
  title: string;
  message: string;
  btnText?: string;
  secondaryText?: string;
  secondaryBtnText?: string;
  loading?: boolean;
  onClick?: () => void;
  onSecondaryClick?: () => void;
  severity?: 'info' | 'warning' | 'success' | 'error';
}

export default function LottieAlert({
  isIn,
  lottiePath,
  loop = false,
  title,
  message,
  btnText,
  secondaryBtnText,
  loading,
  onClick,
  onSecondaryClick,
  severity = 'info',
}: OCRSanningProps) {
  return (
    <WBCollapse in={isIn}>
      <EditViewWrapper severity={severity}>
        <ImageWrapper>
          <ImagePlaceholder>
            {lottiePath && <LottieAnimation path={lottiePath} loop={loop} />}
          </ImagePlaceholder>
          <TextWrapper>
            <Title textAlign={'left'}>{title}</Title>
            <Description textAlign={'left'}>{message}</Description>
          </TextWrapper>
        </ImageWrapper>
        <WBFlex gap={1}>
          {onSecondaryClick && (
            <WBButton
              variant="outlined"
              onClick={onSecondaryClick}
              loading={loading}
              type="button"
            >
              {secondaryBtnText}
            </WBButton>
          )}
          {onClick && (
            <WBButton variant="outlined" type="button" onClick={onClick}>
              {btnText}
            </WBButton>
          )}
        </WBFlex>
      </EditViewWrapper>
    </WBCollapse>
  );
}

const EditViewWrapper = styled(WBFlex, {
  shouldForwardProp: (prop) => prop !== 'severity',
})<{ severity: 'info' | 'warning' | 'success' | 'error' }>(
  ({ theme, severity }) => {
    const backgroundColorMap = {
      info: theme.palette.mode === 'dark' ? '#2E3B4E' : '#E6F6FF',
      warning:
        theme.palette.mode === 'dark'
          ? theme.palette.warning.dark
          : theme.palette.warning.light,
      success:
        theme.palette.mode === 'dark'
          ? theme.palette.success.dark
          : theme.palette.success.light,
      error:
        theme.palette.mode === 'dark'
          ? theme.palette.error.dark
          : theme.palette.error.light,
    } as const;

    const borderColorMap = {
      info: theme.palette.mode === 'dark' ? '#1C2A3A' : '#BCE0F4',
      warning: theme.palette.warning.main,
      success: theme.palette.success.main,
      error: theme.palette.error.main,
    } as const;

    return {
      backgroundColor: backgroundColorMap[severity],
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: theme.spacing(2),
      width: '100%',
      borderRadius: theme.spacing(1),
      borderColor: borderColorMap[severity],
      color: theme.palette.text.primary,
      borderWidth: '1px',
      borderStyle: 'solid',
      padding: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: '100%',
        borderRadius: theme.spacing(1),
        borderWidth: '1px',
        borderStyle: 'solid',
      },
    };
  }
);
const ImageWrapper = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
}));
const ImagePlaceholder = styled(WBFlex)(({ theme }) => ({
  //backgroundColor: theme.palette.grey[300],
  width: '72x',
  height: '72px',
  borderRadius: '100%',
  [theme.breakpoints.down('sm')]: {
    //backgroundColor: theme.palette.grey[300],
    width: '64px',
    height: '64px',
    borderRadius: '100%',
  },
}));
const TextWrapper = styled(WBFlex)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));
const Title = styled(WBTypography)(({ theme }) => ({
  ...theme.typography.h5,
  [theme.breakpoints.down('sm')]: {
    ...theme.typography.h6,
    marginBottom: theme.spacing(0),
  },
}));
const Description = styled(WBTypography)(({ theme }) => ({
  ...theme.typography.body2,
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1),
  },
}));
